import { nextTick, VNode, Ref, onMounted, Slot } from 'vue';
import { useZIndex } from 'element-plus';
import { GridApi, GridOptions } from 'ag-grid-community';
import {
  default as ContextMenu,
  ContextMenuItem,
  ContextMenuGroup
} from '@imengyu/vue3-context-menu';

import { TableCtx, CmdbarApi } from '../types';
import { showError, ifx } from '../../core/Common';
import { useShareData } from '../../core/SharedData';
import { ITableCmdHandles } from '../../core/TableUtilInternal';

import { showDrawer } from '../../core/EasyPopup';
import TableConfigDrawer from '../table-config/TableConfigDrawer.vue';

import { Pagination } from '../../search/types';

// Context Menu
export type TableContextMenu<D> = (
  selectedDatas: D[],
  api: GridApi<D>
) => () => VNode;

type TableProps = {
  contextMenu?: TableContextMenu<any>;
  dblClickHandle?: string;
  suppressRowClickSelection?: boolean;
  showConfig?: 'contextmenu' | true | false;
  pageSizes?: number[];
};

// 表头菜单扩展接口

export type AgHeaderContextMenuExt<T> = {
  init: (
    props: Record<string, any>,
    tableCtx: TableCtx,
    cmdbarApi: CmdbarApi,
    options: {
      pagination: Pagination | undefined;
      submit?: () => Promise<void>;
    }
  ) => T;

  beforeMenus?: (params: T) => (() => JSX.Element) | Slot;

  afterMenus?: (params: T) => (() => JSX.Element) | Slot;
};

// 扩展菜单
let menuExtensions: AgHeaderContextMenuExt<any> | undefined;

export function setExtensionMenus(extension: AgHeaderContextMenuExt<any>) {
  menuExtensions = extension;
}

// 构造表头右键菜单
function buildHeaderContextMenu(
  props: TableProps,
  tableCtx: TableCtx,
  cmdbarApi: CmdbarApi,
  options: {
    pagination: Pagination | undefined;
    submit?: () => Promise<void>;
  },
  extensionCtx: any // 扩展菜单
) {
  return () => {
    const currentPageSize = options.pagination?.size;

    return (
      <>
        {ifx(menuExtensions?.beforeMenus) &&
          menuExtensions!.beforeMenus!(extensionCtx)()}

        <ContextMenuItem
          label="列设置"
          onClick={() => showColumnConfig(tableCtx, cmdbarApi)}
        />
        {ifx(props.pageSizes?.length) && (
          <ContextMenuGroup label="分页大小">
            {props!.pageSizes!.map((size) => (
              <ContextMenuItem
                label={'' + size}
                checked={size == currentPageSize}
                onClick={() => changePageSize(size, options)}
              />
            ))}
          </ContextMenuGroup>
        )}

        {ifx(menuExtensions?.afterMenus) &&
          menuExtensions!.afterMenus!(extensionCtx)()}
      </>
    ).children as VNode[];
  };
}

function changePageSize(
  size: number,
  options: {
    pagination: Pagination | undefined;
    submit?: () => Promise<void>;
  }
) {
  options.pagination!.size = size;
  nextTick(options.submit);
}

async function showColumnConfig(tableCtx: TableCtx, cmdbarApi: CmdbarApi) {
  await showDrawer(
    TableConfigDrawer,
    {
      showCmdConfig: false,
      tableCtx,
      cmdbarApi
    },
    { defaultFooter: false, title: '列表设置', closeOnClickModal: true }
  ).catch(showError);
}

export function useContextMenu(
  tableRef: Ref<any>, // ag-grid对象
  props: TableProps,
  options: GridOptions<any>,
  ext?: {
    submit: () => Promise<void>;
  }
) {
  const { nextZIndex } = useZIndex();

  // 表头右键菜单
  if (props.showConfig == 'contextmenu') {
    const tableCtx = useShareData('table', 'ctx') as TableCtx;
    const cmdbarApi = useShareData('cmdbar', 'setting') as CmdbarApi;

    let pagination: Pagination | undefined;
    if (props.pageSizes?.length) {
      pagination = useShareData('search', 'pagination') as Pagination;
    }

    // 菜单扩展初始化
    const extensionCtx = menuExtensions?.init(props, tableCtx, cmdbarApi, {
      pagination,
      submit: ext?.submit
    });

    onMounted(() => {
      const table = tableRef.value.$el as HTMLElement;
      const header = table.querySelector('.ag-header');

      header?.addEventListener('contextmenu', (event) => {
        const evt = event as MouseEvent;
        evt.preventDefault();

        const menus = buildHeaderContextMenu(
          props,
          tableCtx,
          cmdbarApi,
          {
            pagination,
            submit: ext?.submit
          },
          extensionCtx
        );

        const zIndex = nextZIndex();
        ContextMenu.showContextMenu(
          {
            x: evt.x,
            y: evt.y,
            items: [],
            closeWhenScroll: false,
            zIndex
          },
          { default: menus }
        );
      });
    });
  }

  // 右键菜单, 从SearchPanel提取cmd handles
  const tableCmd = useShareData('table', 'cmd') as {
    handles: ITableCmdHandles;
  };

  // 没有菜单
  if (!tableCmd) {
    return;
  }

  if (props.contextMenu) {
    const onCellContextMenu = options.onCellContextMenu;
    options.onCellContextMenu = (event) => {
      onCellContextMenu?.(event);

      const evt = event.event! as MouseEvent;
      evt.preventDefault();

      if (!event.node.isSelected()) {
        // 只选中当前行
        event.node.setSelected(true, true);
      }

      const api = event.api;

      const items = props.contextMenu!(api.getSelectedRows(), api);
      const slot = () => {
        return items()?.children as VNode[];
      };

      const zIndex = nextZIndex();
      ContextMenu.showContextMenu(
        {
          x: evt.x,
          y: evt.y,
          items: [],
          closeWhenScroll: false,
          zIndex
        },
        { default: slot }
      );
    };
  }

  // 双击行 操作
  if (props.dblClickHandle && !props.suppressRowClickSelection) {
    const onRowDoubleClicked = options.onRowDoubleClicked;
    options.onRowDoubleClicked = async (event) => {
      await tableCmd.handles.execute(props.dblClickHandle!);

      onRowDoubleClicked?.(event);
    };
  }
}
